import React from 'react';
import { Typography } from '@saucelabs/ds-typography';
import { Rocker } from '@saucelabs/ds-inputs';
import { OverlayTrigger, Tooltip } from '@saucelabs/sl-component-overlay';
import { IconQuestionOutline } from '@saucelabs/ds-icons';

import { ConcurrencySlider } from './ConcurrencySlider';
import { amountSeparator, formatThousand } from '../../utils/numbers';
import { projectsToMonthlyExecutions } from '../../utils/plans';
import { PLAN_INTERVAL } from '../../constants';
import Styles from './Product.styles';

const Product: {
  ApiTestingProjects: React.FC<ProductApiTestingProjectsProps>;
  Concurrency: React.FC<ProductConcurrencyProps>;
  Description: React.FC<ProductSloganProps>;
  Header: React.FC<ProductHeaderProps>;
  IntervalChoice: React.FC<ProductIntervalChoiceProps>;
  MonthlyRate: React.FC<ProductMonthlyRateProps>;
  MonthlyRateUsage: React.FC<ProductMonthlyRateProps>;
  Name: React.FC<ProductNameProps>;
  Slogan: React.FC<ProductSloganProps>,
} = {};

interface ProductHeaderProps {
  children: React.ReactNode;
}
Product.Header = ({ children }) => (
  <header className="flex justify-between">
    {children}
  </header>
);
Product.Header.displayName = 'ProductHeader';

interface ProductNameProps {
  children: React.ReactNode;
}
Product.Name = ({ children }) => (
  <Typography
    className={`${Styles.name} my-0`}
    disableSpacing
    variant="heading-4"
  >
    {children}
  </Typography>
);
Product.Name.displayName = 'ProductName';

interface ProductIntervalChoiceProps {
  id: string;
  onChange?: (arg0: boolean) => void;
  saving: string;
  selectedInterval: PlanInterval;
}
Product.IntervalChoice = ({ id, onChange, selectedInterval, saving }) => (
  <div
    className="flex justify-end flex-nowrap text-sm"
    data-qa="product-interval-choice"
    id={id}
  >
    <Rocker
      endLabel="Monthly"
      isSelected={selectedInterval === PLAN_INTERVAL.MONTH}
      onChange={(status) => onChange?.(status)}
      startLabel={(
        <>
          {'Annual '}
          <span className="text-green-600 font-bold">
            (save
            {' '}
            {saving}
            )
          </span>
        </>
      )}
    />
  </div>
);
Product.IntervalChoice.displayName = 'ProductIntervalChoice';

interface ProductSloganProps {
  children: React.ReactNode;
}
Product.Slogan = ({ children }) => (
  <p className={Styles.slogan}>
    {children}
  </p>
);
Product.Slogan.displayName = 'ProductSlogan';

interface ProductDescriptionProps {
  children: React.ReactNode;
}
Product.Description = ({ children }) => (
  <div
    className={Styles.description}
    data-qa="product-description"
  >
    {children}
  </div>
);
Product.Description.displayName = 'ProductDescription';

interface ProductConcurrencyProps {
  allowedValues: number[];
  currentValue: number;
  subscriptionValue?: number;
  tooltipText?: string;
  updateConcurrency?: () => void;
}
Product.Concurrency = ({
  allowedValues,
  currentValue,
  subscriptionValue,
  tooltipText = null,
  updateConcurrency = null,
}) => (
  <div className="w-6/12 mb-4">
    <div className="pb-1 text-gray-600 font-bold">
      <span className="text-blue-700 text-base">{currentValue}</span>
      {' parallel tests'}
      <OverlayTrigger
        id="concurrency-slider-explanation"
        overlay={<Tooltip id="concurrency-tooltip-explanation">{tooltipText}</Tooltip>}
        placement="top"
      >
        <IconQuestionOutline
          className={`align-text-bottom ml-1 ${Styles.tooltip}`}
          height="16"
          width="16"
        />
      </OverlayTrigger>
    </div>
    <ConcurrencySlider
      allowedValues={allowedValues}
      currentValue={currentValue}
      onChange={updateConcurrency}
      referenceValue={subscriptionValue}
      ticksValues={allowedValues.filter((number) => number % 4 === 0)}
    />
  </div>
);
Product.Concurrency.displayName = 'ProductConcurrency';

interface ProductApiTestingProjectsProps {
  allowedValues: number[];
  currentValue: number;
  plan: Plan;
  subscriptionValue?: number;
  updateProjects?: () => void;
}
Product.ApiTestingProjects = ({
  allowedValues,
  currentValue,
  plan,
  subscriptionValue = null,
  updateProjects = null,
}) => (
  <div className="w-6/12 mb-4">
    <div className="pb-1 text-gray-600 text-sm font-semibold">
      <span className="text-blue-700">{currentValue}</span>
      {' projects, '}
      {formatThousand(projectsToMonthlyExecutions(currentValue, plan.add_ons))}
      {' executions/mo'}
    </div>
    <ConcurrencySlider
      allowedValues={allowedValues}
      className="relative ml-1.5"
      currentValue={currentValue}
      onChange={updateProjects}
      referenceValue={subscriptionValue}
    />
  </div>
);
Product.ApiTestingProjects.displayName = 'ProductApiTestingProjects';

interface ProductMonthlyRateProps {
  rate?: number;
}
Product.MonthlyRate = ({ rate = null }) => (
  <div>
    <mark className={Styles.rate}>{`${amountSeparator(rate) ?? 'n/a'}`}</mark>
    <span className={Styles['per-month']}>/ month</span>
  </div>
);
Product.MonthlyRate.displayName = 'ProductMonthlyRate';

Product.MonthlyRateUsage = ({ rate = null }) => (
  <div
    className="text-right text-high-emphasis"
    data-qa="usage-monthly-rate"
  >
    <Typography
      className="font-semibold mt-0 mb-0.5"
      disableSpacing
      variant="heading-2"
    >
      {`${amountSeparator(rate) ?? 'n/a'}`}
      <span className="m-0 text-sm font-normal">/month</span>
    </Typography>
  </div>
);
Product.MonthlyRateUsage.displayName = 'ProductMonthlyRate';

export { Product };
export default Product;
